import { PillSelect } from "@/components/we360/PillSelect";
import { AlertBanner } from "@/components/we360/AlertBanner";
import { CalculatedBadge } from "@/components/we360/CalculatedBadge";
import { CheckOption } from "@/components/we360/CheckOption";
import { ParQSVG } from "./SVGIllustrations";
import { PARQ_QUESTIONS } from "./helpers";
import { Pill, AlertTriangle } from "lucide-react";

export default function Step2ParQ2({
  parqResponses,
  setParqResponse,
  parqOverride,
  setParqOverride,
  allParqAnswered,
  parqAlerta,
  parqRiskCount,
  medicamentos = [],
  parqMedsConfirmed = false,
  setParqMedsConfirmed,
}) {
  const hasF1Meds = Array.isArray(medicamentos) && medicamentos.length > 0;
  const hasCardioAtcMed = hasF1Meds && medicamentos.some((m) => String(m?.codigoATC || m?.atcCode || "").toUpperCase().startsWith("C"));

  return (
    <div>
      <div className="text-center mb-3">
        <ParQSVG hasAlert={allParqAnswered && parqAlerta} />
      </div>

      {PARQ_QUESTIONS.slice(4, 7).map((q, i) => {
        const qIndex = i + 4;
        const isMedQuestion = qIndex === 5;
        const answeredNoOnMeds = isMedQuestion && parqResponses[5] === false && hasF1Meds;

        return (
          <div key={qIndex} className="mb-3">
            <p className="text-xs font-medium mb-1.5" style={{ color: "#2D2A24" }}>
              {qIndex + 1}. {q}
            </p>

            {isMedQuestion && hasF1Meds && (
              <div
                className="mb-2.5 p-3 rounded-2xl text-left border"
                style={{
                  background: "rgba(0, 98, 143, 0.05)",
                  borderColor: "rgba(0, 98, 143, 0.18)",
                }}
                data-testid="parq-f1-meds-reminder"
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Pill size={13} style={{ color: "#00628F" }} />
                  <span className="text-[11px] font-bold" style={{ color: "#00628F" }}>
                    Recordatorio de tu perfil F1 ({medicamentos.length} medicamento{medicamentos.length > 1 ? "s" : ""} asociado{medicamentos.length > 1 ? "s" : ""})
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {medicamentos.map((m, idx) => {
                    const name = m.nombre || m.name || "Medicamento";
                    const dose = m.dosis || m.dosificacion || m.dosage || "";
                    const atc = String(m.codigoATC || m.atcCode || "").toUpperCase();
                    const isCardio = atc.startsWith("C");
                    return (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{
                          background: isCardio ? "#FEF3C7" : "#FFFFFF",
                          color: isCardio ? "#92400E" : "#2D2A24",
                          border: `1px solid ${isCardio ? "#F59E0B" : "rgba(0,98,143,0.2)"}`,
                        }}
                      >
                        {name}{dose ? ` (${dose})` : ""}
                        {isCardio && <span className="text-[9px] font-bold uppercase ml-0.5">• Cardiovascular</span>}
                      </span>
                    );
                  })}
                </div>
                <p className="text-[10.5px] leading-snug" style={{ color: "#5C5343" }}>
                  Esta pregunta evalúa únicamente si alguno de tus medicamentos es para la <strong>presión arterial o el corazón</strong>. Tus medicamentos registrados en F1 se conservarán intactos en tu perfil.
                </p>
              </div>
            )}

            <PillSelect
              options={["Sí", "No"]}
              value={parqResponses[qIndex] === null ? "" : parqResponses[qIndex] ? "Sí" : "No"}
              onChange={(v) => {
                const isYes = v === "Sí";
                setParqResponse(qIndex, isYes);
                if (isMedQuestion && isYes && setParqMedsConfirmed) {
                  setParqMedsConfirmed(false);
                }
              }}
              testId={`parq-${qIndex + 1}`}
              columns={2}
            />

            {answeredNoOnMeds && (
              <div
                className="mt-2.5 p-3.5 rounded-2xl text-left border"
                style={{
                  background: "#FFFBEB",
                  borderColor: "#FCD34D",
                }}
                data-testid="parq-f1-meds-confirm-box"
              >
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" style={{ color: "#D97706" }} />
                  <p className="text-[11px] font-semibold leading-snug" style={{ color: "#92400E" }}>
                    {hasCardioAtcMed
                      ? "Detectamos en tu F1 al menos un medicamento clasificado en el grupo cardiovascular. Por favor verifica si es para la presión arterial o el corazón antes de confirmar 'No'."
                      : "Según tu F1 tienes medicamentos asociados a tu perfil. Confirma por favor que ninguno de ellos es para la presión arterial o el corazón."}
                  </p>
                </div>
                <CheckOption
                  label="Confirmo que mis medicamentos de F1 continúan activos en mi perfil, pero ninguno es para la presión arterial o el corazón."
                  checked={parqMedsConfirmed}
                  onChange={() => setParqMedsConfirmed && setParqMedsConfirmed(!parqMedsConfirmed)}
                  testId="parq-f1-meds-confirm-check"
                />
              </div>
            )}
          </div>
        );
      })}

      {allParqAnswered && (
        <div className="mt-4">
          {parqAlerta ? (
            <>
              <AlertBanner variant="danger" testId="parq-alert">
                Has respondido 'Sí' a {parqRiskCount} pregunta(s) del PAR-Q.
                Te recomendamos consultar a tu médico antes de realizar las pruebas físicas.
                No avances sin autorización médica.
              </AlertBanner>
              <div className="mt-3">
                <CheckOption
                  label="Entiendo el riesgo y deseo continuar bajo mi propia responsabilidad."
                  checked={parqOverride}
                  onChange={() => setParqOverride(!parqOverride)}
                  testId="parq-override-check"
                />
              </div>
            </>
          ) : (
            <CalculatedBadge
              label="PAR-Q"
              value="Apto"
              category="Pruebas físicas"
              categoryColor="#157346"
              testId="parq-clearance-badge"
            />
          )}
        </div>
      )}
    </div>
  );
}

